import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

export default function WinnersAndLosers() {
  const [results, setResults] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nearestPin, setNearestPin] = useState("");
  const [nearestPin2, setNearestPin2] = useState("");
  const [longestDrive, setLongestDrive] = useState("");
  const [toast, setToast] = useState("");
  const [raffleNumbers, setRaffleNumbers] = useState({});
  const [raffleWinner, setRaffleWinner] = useState(null);

  useEffect(() => { loadResults(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadResults = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) return;
      const ev = eventRes.data;
      setEvent(ev);
      const prizesRes = await api.get("/events/prizes");
      if (prizesRes.data) {
        setNearestPin(prizesRes.data.nearest_pin || "");
        setNearestPin2(prizesRes.data.nearest_pin2 || "");
        setLongestDrive(prizesRes.data.longest_drive || "");
      }
      const [scoresRes, groupsRes, holesRes] = await Promise.all([
        api.get("/scores/" + ev.id),
        api.get("/groups"),
        api.get("/courses/" + ev.course_id + "/holes")
      ]);
      const scores = scoresRes.data;
      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holeData = holesRes.data;
      const isMedal = ev.game_type?.includes("Medal") || ev.game_type?.includes("Stroke");

      const playerResults = players.map(player => {
        const playerScores = scores.filter(s => s.player_id === player.player_id);
        let gross = 0, points = 0, net = 0;
        const holePoints = [];
        const holeGross = [];
        holeData.forEach(hole => {
          const s = playerScores.find(s => s.hole === hole.hole);
          if (s) {
            const hcp = player.handicap || 0;
            const base = Math.floor(hcp / 18);
            const extra = hcp % 18;
            const shots = base + (hole.stroke_index <= extra ? 1 : 0);
            const g = parseInt(s.gross);
            const n = g - shots;
            const pts = Math.max(0, 2 + (hole.par - n));
            gross += g; net += n; points += pts;
            holePoints.push(pts);
            holeGross.push(g);
          } else {
            holePoints.push(0);
            holeGross.push(0);
          }
        });
        const back9 = holePoints.slice(9).reduce((a,b) => a+b, 0);
        const last6 = holePoints.slice(12).reduce((a,b) => a+b, 0);
        const last3 = holePoints.slice(15).reduce((a,b) => a+b, 0);
        const last1 = holePoints[17] || 0;
        return { ...player, gross: gross||0, net: net||0, points: points||0, thru: playerScores.length, back9, last6, last3, last1, holeGross };
      }).filter(p => p.thru === 18);

      playerResults.sort((a, b) => {
        if (isMedal) {
          if (a.net !== b.net) return a.net - b.net;
          if (a.back9 !== b.back9) return a.back9 - b.back9;
          if (a.last6 !== b.last6) return a.last6 - b.last6;
          if (a.last3 !== b.last3) return a.last3 - b.last3;
          return a.last1 - b.last1;
        } else {
          if (a.points !== b.points) return b.points - a.points;
          if (a.back9 !== b.back9) return b.back9 - a.back9;
          if (a.last6 !== b.last6) return b.last6 - a.last6;
          if (a.last3 !== b.last3) return b.last3 - a.last3;
          return b.last1 - a.last1;
        }
      });

      let pos = 1;
      playerResults.forEach((p, i) => {
        if (i === 0) { p.pos = 1; }
        else {
          const prev = playerResults[i-1];
          p.pos = pos;
        }
        pos = i + 2;
      });

      setResults(playerResults);
      const nonPrize = playerResults.filter(p => p.pos > 2);
      const shuffled = [...nonPrize].sort(() => Math.random() - 0.5);
      const numbers = {};
      shuffled.forEach((p, i) => { numbers[p.player_id] = i + 1; });
      setRaffleNumbers(numbers);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const spinWheel = () => {
    if (raffleEligible.length === 0) return;
    const winner = raffleEligible[Math.floor(Math.random() * raffleEligible.length)];
    setRaffleWinner(winner.player_id);
    showToast("Winner: " + winner.name + " (No. " + raffleNumbers[winner.player_id] + ")");
  };

  const savePrizes = async () => {
    try {
      await api.put("/events/prizes", { nearest_pin: nearestPin, nearest_pin2: nearestPin2, longest_drive: longestDrive });
      showToast("Prizes saved!");
    } catch(e) { showToast("Failed to save prizes: " + (e.response?.data?.error || e.message)); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");
  const top2 = results.filter(p => p.pos <= 2);
  const twosList = results.flatMap(p => (p.holeGross || []).map((g, i) => g === 2 ? { name: p.name, hole: i + 1 } : null).filter(Boolean));
  const prizeWinners = [nearestPin, nearestPin2, longestDrive, ...twosList.map(t => t.name)].filter(Boolean).map(n => n.toLowerCase().trim());
  // Everyone not 1st/2nd and not already a prize winner (nearest pin,
  // longest drive, or a two) enters the raffle — including 3rd place now,
  // since only the top two get a podium finish.
  const raffleEligible = results.filter(p => p.pos > 2 && !prizeWinners.includes(p.name.toLowerCase().trim()));

  const goldBg = { background: "#FFD700", fontWeight: "bold" };
  const silverBg = { background: "#E8E8E8", fontWeight: "bold" };
  const bronzeBg = { background: "#F4A460", fontWeight: "bold" };
  const getRowStyle = (pos) => pos === 1 ? goldBg : pos === 2 ? silverBg : pos === 3 ? bronzeBg : {};
  const posLabel = (pos) => pos === 1 ? "1st" : pos === 2 ? "2nd" : pos === 3 ? "3rd" : pos + "th";

  return (
    <Layout>
      <div style={{ padding: "16px", maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <div>
            <h1 style={{ fontSize: "18px", fontWeight: "800", color: "#14532d" }}>Winners and Losers</h1>
            {event && <div style={{ fontSize: "12px", color: "#6b7280" }}>{event.game_type}</div>}
          </div>
          <button onClick={savePrizes} style={{ background: "#14532d", color: "white", border: "none", padding: "8px 20px", borderRadius: "6px", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>Save Prizes</button>
        </div>

        <div style={{ background: "white", borderRadius: "8px", padding: "16px", marginBottom: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>
          <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", marginBottom: "12px", borderLeft: "4px solid #16a34a", paddingLeft: "10px" }}>PRIZE WINNERS</h2>
          {loading && <div style={{ color: "#9ca3af", fontSize: "13px" }}>Loading results...</div>}
          {!loading && top2.length === 0 && <div style={{ color: "#9ca3af", fontSize: "13px" }}>No completed results yet.</div>}
          {top2.map(p => (
            <div key={p.player_id} style={{ ...getRowStyle(p.pos), display: "flex", alignItems: "center", padding: "10px 16px", borderRadius: "6px", marginBottom: "6px" }}>
              <div style={{ width: "140px", fontSize: "13px", fontWeight: "700" }}>{posLabel(p.pos)} {event?.game_type}</div>
              <div style={{ flex: 1, fontSize: "15px", fontWeight: "700" }}>{p.name}</div>
              <div style={{ fontSize: "13px", fontWeight: "700" }}>{isMedal ? p.net + " net" : p.points + " pts"}</div>
            </div>
          ))}
          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {[
              { label: "Nearest Pin", value: nearestPin, set: setNearestPin },
              { label: "Nearest Pin in 2", value: nearestPin2, set: setNearestPin2 },
              { label: "Longest Drive", value: longestDrive, set: setLongestDrive }
            ].map(({ label, value, set }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", padding: "8px 16px", background: "#f0fdf4", borderRadius: "6px", gap: "16px" }}>
                <div style={{ width: "160px", fontSize: "13px", fontWeight: "600", color: "#374151" }}>{label}</div>
                <input type="text" value={value} onChange={e => set(e.target.value)} placeholder="Enter player name" style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: "4px", padding: "6px 10px", fontSize: "13px" }} />
              </div>
            ))}
          </div>
          {twosList.length > 0 && (
            <div style={{ marginTop: "12px", padding: "10px 16px", background: "#faf5ff", borderRadius: "6px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#6b21a8", marginBottom: "4px" }}>🎯 Twos</div>
              <div style={{ fontSize: "13px", color: "#374151" }}>{twosList.map(t => t.name + " (Hole " + t.hole + ")").join(", ")}</div>
            </div>
          )}
        </div>

        <div style={{ background: "white", borderRadius: "8px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <h2 style={{ fontSize: "14px", fontWeight: "700", color: "#14532d", borderLeft: "4px solid #16a34a", paddingLeft: "10px" }}>LOSERS RAFFLE - Non-Prize Players</h2>
            <button onClick={spinWheel} style={{ background: "#7c3aed", color: "white", border: "none", padding: "6px 16px", borderRadius: "4px", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>Spin The Wheel</button>
          </div>
          <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>These players are eligible for the Losers Raffle prize draw.</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#14532d", color: "white" }}>
                <th style={{ padding: "8px 12px", textAlign: "left" }}>Player</th>
                <th style={{ padding: "8px 12px", textAlign: "center" }}>{isMedal ? "Net" : "Points"}</th>
                <th style={{ padding: "8px 12px", textAlign: "center" }}>Raffle No</th>
              </tr>
            </thead>
            <tbody>
              {raffleEligible.map((p, i) => (
                <tr key={p.player_id} style={{ background: raffleWinner === p.player_id ? "#fef08a" : i % 2 === 0 ? "white" : "#f0fdf4", borderBottom: "1px solid #e5e7eb", fontWeight: raffleWinner === p.player_id ? "700" : "normal" }}>
                  <td style={{ padding: "8px 12px", fontWeight: "500" }}>{p.name}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>{isMedal ? p.net : p.points}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: "700", color: "#14532d" }}>{raffleNumbers[p.player_id] || "-"}</td>
                </tr>
              ))}
              {raffleEligible.length === 0 && <tr><td colSpan="3" style={{ padding: "16px", textAlign: "center", color: "#9ca3af" }}>No raffle eligible players yet.</td></tr>}
            </tbody>
          </table>
        </div>

        {toast && <div style={{ position: "fixed", bottom: "20px", right: "20px", background: "#14532d", color: "white", padding: "12px 20px", borderRadius: "8px", fontWeight: "700", fontSize: "13px", zIndex: 9999 }}>{toast}</div>}
      </div>
    </Layout>
  );
}
